// University of Louisville: public college page (#2748), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UofL's Common Data Set 2024-2025, the edition its Common Data Set page
//    calls "Current edition" (no 2025-2026 file was posted on 2026-10-01).
//    The PDF is served from UofL's Widen asset host
//    (louisville.widen.net, "Common-Data-Set-2024-2025-final"), so the
//    SOURCES url is the louisville.edu page that links it. C1 applicants,
//    admits and enrollees for Fall 2024 with the residency table; C8 test
//    policy (stated for Fall 2026); C21/C22 (no early decision, no early
//    action).
//  - UofL Undergraduate Admissions: Getting Help and FAQs (the 2027
//    application, the February 15, 2027 priority deadline, decision timing),
//    Standardized Test Score Submission Options, and Program-Specific
//    Admissions Criteria. The criteria page carries no year; it is the page
//    as posted while the 2027 application is open.
//  - UofL Student Financial Aid, Cost of Attendance. The newest year it
//    posts is 2025-2026 (Undergraduate, Non-Online Programs, On-Campus,
//    academic year), so the cost is for 2025-2026; its items sum exactly to
//    its totals. The Bursar's Annual Undergraduate Tuition and Fees page
//    already lists 2026-2027 tuition and fees ($13,614 resident, $29,960
//    out of state), but no full 2026-2027 cost of attendance.
//  - College Scorecard, school id 157289 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UofL Common Data Set 2024-2025',
        url: 'https://louisville.edu/about/departments/academic-planning-accountability/university-data/common-data-set',
        read: '2026-10-01',
    },
    faqs: {
        label: 'UofL Undergraduate Admissions, Getting Help and FAQs',
        url: 'https://louisville.edu/admissions/apply/getting-help-faqs',
        read: '2026-10-01',
    },
    tests: {
        label: 'UofL Undergraduate Admissions, Standardized Test Score Submission Options',
        url: 'https://louisville.edu/admissions/apply/standardized-test-submission-options',
        read: '2026-10-01',
    },
    programs: {
        label: 'UofL Undergraduate Admissions, Program-Specific Admissions Criteria',
        url: 'https://louisville.edu/admissions/apply/program-specific-admissions-criteria',
        read: '2026-10-01',
    },
    costs: {
        label: 'UofL Student Financial Aid, Cost of Attendance',
        url: 'https://louisville.edu/financialaid/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?157289-University-of-Louisville',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-louisville',
    kbId: 'university_of_louisville',
    name: 'University of Louisville',
    shortName: 'Louisville',
    aliases: ['UofL'],
    location: 'Louisville, KY',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UofL brand color page); decoration only.
    branding: {
        primary: '#AD0000',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_louisville_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: '2024-2025',
        applied: 15668,
        admitted: 12442,
        enrolled: 3120,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 8756, admitted: 6982 },
            { group: 'Out-of-state', applied: 6474, admitted: 5284 },
            { group: 'International', applied: 438, admitted: 176 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UofL\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UofL has no early decision or early action plan. Its February 15 priority deadline is a filing date, not an early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Each school or program sets the weighted GPA and scores for automatic admission, and a path without scores:',
        rows: [
            { school: 'Arts and Sciences, Education, Public Health, Social Work', requirement: 'Automatic admission with a 2.5 GPA and a 1030 SAT or 20 ACT. Without scores: a 2.5 GPA and a full review of your transcript. Social Work admits freshmen to a lower division first.' },
            { school: 'Business majors (Accountancy, Computer Information Systems, Economics, Equine Business, Finance, Global Supply Chain Management, Management, Marketing)', requirement: 'Automatic admission with a 3.0 GPA and a 1200 SAT or 25 ACT, or a 3.3 GPA and a 1100 SAT or 22 ACT, plus a 540 SAT math or 22 ACT math score. Without scores: a 3.0 GPA, Algebra 2 done, A and B grades in math from 10th grade and an advanced math course.' },
            { school: 'Business Administration', requirement: 'Automatic admission with a 2.75 GPA, a 1130 SAT or 23 ACT and a 540 SAT math or 22 ACT math score. Without scores: a 2.75 GPA, Algebra 2 done and A and B grades in math from 10th grade.' },
            { school: 'Engineering', requirement: 'Automatic admission with a 3.0 GPA, a 1200 SAT or 25 ACT and a 590 SAT math or 25 ACT math score, or a 3.5 GPA, a 1160 SAT or 24 ACT and a 580 SAT math or 24 ACT math score. Without scores: a 3.5 unweighted GPA, college algebra or higher (or AP or IB precalculus or calculus with a 3 or better), and calculus or precalculus plus chemistry. Computer Science BA and Applied Engineering do not need chemistry.' },
            { school: 'Nursing', requirement: 'Automatic admission with a 3.0 GPA, a 1100 SAT or 22 ACT and a 540 SAT math or 22 ACT math score. Without scores: a 3.5 GPA, four years of English with a 3.0 and an advanced math course in junior or senior year. Students not admitted directly start in a lower division and apply to the upper division after 3 to 4 semesters.' },
            { school: 'Music', requirement: 'Extra requirements that could include an audition, portfolio review, interview or vocal assessment.' },
        ],
        note: 'Engineering, Nursing and Business applicants who have the GPA but not the score can show readiness through the Accuplacer.',
        source: 'programs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'February 15, 2027' },
        rows: [
            { plan: 'Priority deadline', deadline: 'February 15, 2027', decision: 'Usually 5 to 7 business days after your file is complete', binding: false },
        ],
        note: 'The 2027 application is open. Competitive and mentored scholarship applications are due December 1, and the housing application opens November 2 for admitted students. UofL publishes no fixed decision date.',
        source: 'faqs',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'UofL is test-optional for admission to every major, and every program has a path without the SAT or ACT. If you send scores, UofL uses your highest SAT or ACT composite and does not superscore. The Honors Program, which has its own application, requires the SAT or ACT. If you are admitted without scores, you take the Accuplacer math and reading tests for placement.',
        source: 'tests',
        cdsLine: 'The 2024-2025 Common Data Set marks the SAT or ACT as not required, but considered if submitted, for fall 2026 applicants, with scores due by August 18.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'tests',
    },

    cost: {
        year: '2025-2026',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [13390, 29736] },
            { item: 'Loan fees', amounts: [68, 68] },
            { item: 'Housing and food (on-campus living expense)', amounts: [12960, 12960] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1190, 1190] },
            { item: 'Transportation', amounts: [2000, 2000] },
            { item: 'Personal expenses', amounts: [6362, 6362] },
        ],
        totals: [35970, 52316],
        note: 'For a full-time undergraduate living on campus, fall and spring, in 2025-2026, the newest year the financial aid office posts. The 2026-2027 figures were not yet posted.',
        source: 'costs',
        netPrice: {
            average: 14960,
            byIncome: [
                { income: '$0 to $30,000', amount: 12104 },
                { income: '$30,001 to $48,000', amount: 11100 },
                { income: '$48,001 to $75,000', amount: 12680 },
                { income: '$75,001 to $110,000', amount: 16542 },
                { income: 'Over $110,000', amount: 23040 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UofL\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
